import { URL } from '@ux/util';
import psl, { isValid } from './psl';

/**
 * Add query parameters from props to the url.
 *
 * @param {String} url Logout or login link.
 * @param {Object} props Properties to merge with the query parameters object.
 * @returns {String} Configured url.
 * @api private
 */
function updateQuery(url, props) {
  url = new URL(url, true);

  return url.set('query', { ...url.query, ...props }).href;
}

/**
 * This function takes in a currentHref
 * and will return that with no query parameters OR if a
 * currentPage query parameter is present, it will utilize
 * that value and pass it back.
 *
 * @param {String} currentHref The currentHref usually passed thru props
 * @param {Bool} isBrowser A boolean indicating whether context is a browser
 * @param {String} location A string that represents document.location or window.location
 * @returns {String} A currentHref string without query params -OR- the value of the currentPage query parameter
 */
function updateCurrentHref(currentHref, isBrowser, location) {
  //
  // Logic originally from
  // https://github.secureserver.net/UX/product-nav/blob/119ebd4/lib/product-nav.js#L37
  //
  if (isBrowser) {
    currentHref = location;
  }

  if (currentHref) {
    const currentUrl = new URL(currentHref, true);
    const qsCurrentHref = currentUrl.query.currentPage;
    if (qsCurrentHref) {
      currentHref = qsCurrentHref;
    } else {
      // Remove query string for comparisons
      currentUrl.set('query', {});
      currentHref = currentUrl.href;
    }
  }

  return currentHref;
}

/**
 * This is a utility function to make sure that the GUI href is pointing to same domain where the browser is currently at.
 * If the top level domain is not the same then a new gui url is generated.
 *
 * @param {String} guiUrl gui url to verify
 * @returns {String} Fixed gui url which is pointing within the current top level domain
 */
function fixGuiUrl(guiUrl) {
  if (!window?.location?.hostname) {
    return guiUrl;
  }

  try {
    const currentLocationHostname = window.location.hostname;
    const parsedCurrentLocationDomain = psl.parse(currentLocationHostname).domain;

    if (!isValid(parsedCurrentLocationDomain)) {
      // the current hostname is not one of the known public suffixes, return the original gui url;
      return guiUrl;
    }

    const guiURLObject = new URL(guiUrl);
    const guiHostname = guiURLObject.hostname;
    const parsedGuiDomain = psl.parse(guiHostname).domain;

    if (parsedCurrentLocationDomain === parsedGuiDomain) {
      // the request to gui is on same domain as where the browser is currently at
      return guiUrl;
    }

    return `${guiURLObject.protocol}//gui.${parsedCurrentLocationDomain}${guiURLObject.pathname}`;
  } catch (err) {
    // parsing of the domain has errored, fallback to original url
    return guiUrl;
  }
}

export {
  updateQuery,
  updateCurrentHref,
  fixGuiUrl
};
